import { useCallback, useMemo, useState } from "react";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { Sidebar } from "../../../containers/Sidebar/Sidebar";
import useStyles from "./styles";
import { CustomTable } from "../../../components/CustomTable/CustomTable";
import { CustomButtonTable } from "../../../components/CustomButtonTable/CustomButtonTable";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import {
  TVehicleListFilters,
  useGetAllVehiculesFleet,
  useGetVehicleFilterOptions,
} from "../../../api/vehicule";
import { CustomLoader } from "../../../components/CustomLoader/CustomLoader";
import { getColumnsListVehicle } from "./columnsListVehicle";
import { useServerPagination } from "../../../hooks/useServerPagination";
import { VehicleFilters } from "./VehicleFilters";

type Props = {};

export const ListVehicule: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fleetId = getCurrentFleetId();
  const pagination = useServerPagination();

  const [filters, setFilters] = useState<TVehicleListFilters>({
    sort: "plate_asc",
  });

  const { options } = useGetVehicleFilterOptions(fleetId);

  // Keyed on `t`: react-i18next hands back a new `t` identity on every
  // language change, so the headers and status chips rebuild in the new
  // language instead of staying frozen at their import-time values.
  const columns = useMemo(
    () =>
      getColumnsListVehicle((key: string, fallback?: string) =>
        fallback ? t(key, { defaultValue: fallback }) : t(key)
      ),
    [t]
  );

  const { vehicules, totalCount, isPending } = useGetAllVehiculesFleet(
    fleetId,
    pagination.page,
    pagination.perPage,
    pagination.search,
    "",
    filters
  );

  /**
   * Any filter change restarts at page 1.
   *
   * Filtering is server-side and paginated, so keeping the page index would
   * ask for page 7 of a result set that may now have two pages — and the API
   * answers an empty list, which reads as "no vehicles match" when the real
   * answer is "you are past the end".
   */
  const applyFilter = useCallback(
    (partial: Partial<TVehicleListFilters>) => {
      setFilters((prev) => ({ ...prev, ...partial }));
      pagination.resetToFirstPage();
    },
    [pagination]
  );

  const resetFilters = useCallback(() => {
    setFilters({ sort: "plate_asc" });
    pagination.resetToFirstPage();
  }, [pagination]);

  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}
      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.containerHeader}>
          <CustomTypography
            variant="h2"
            text={t("vehicule.vehicleList", "Vehicle list")}
            isUppercase
          />
          <CustomButtonTable
            variant={"primary"}
            textVariant={"link"}
            label={t("vehicule.addVehicule", "Add vehicle")}
            width={"fit-content"}
            fontWeight={"bold"}
            textDecoration={"none"}
            iconName={"plus"}
            padding={"0px 15px"}
            textTransform="uppercase"
            onClick={() => navigate("/vehicule/create")}
          />
        </div>

        <VehicleFilters
          filters={filters}
          options={options}
          onChange={applyFilter}
          onReset={resetFilters}
          resultCount={totalCount}
        />

        {/* Explicitly classed rather than found by `:last-child` — see
            styles.tableCard. */}
        <div className={classes.tableCard}>
          <CustomTable
            columns={columns}
            data={vehicules}
            isSearchable
            isFilterFromBackend
            onClickButtonEye={() => navigate("/vehicule/update")}
            tablePagnationCount={totalCount}
            {...pagination.tableProps}
          />
        </div>
      </Sidebar>
    </>
  );
};
